import * as Yup from 'yup';
import {
	addDays,
	getDay,
	setHours,
	setMinutes,
	setSeconds,
	setMilliseconds,
	isSameDay,
	parseISO,
	isEqual,
} from 'date-fns';
import convert from 'convert-units';

import type { TestProps } from './test.types';
import { publicHolidays } from '../utils/config';

const status = {
	readyForPayment: 'READY_FOR_PAYMENT',
	readyForSchedule: 'READY_FOR_SCHEDULE',
	readyForAssessment: 'READY_FOR_ASSESSMENT',
	readyForApproval: 'READY_FOR_APPROVAL',
	completed: 'COMPLETED',
	cancelled: 'CANCELLED',
};

const paymentStatus = {
	fullyPaid: 'FULLY_PAID',
	partialPaid: 'PARTIAL_PAID',
	unpaid: 'UNPAID',
	depositPaid: 'DEPOSIT_PAID',
	paymentError: 'PAYMENT_ERROR',
};

export type Status = (typeof status)[keyof typeof status];
export type PaymentStatus = (typeof paymentStatus)[keyof typeof paymentStatus];

export interface RequestProps {
	id: string;
	assessmentDates: Date[];
	prefAssessmentDates: (Date | null)[];
	status: Status;
	supplierId: string;
	tests: TestProps[];
	candidate: CandidateProps;
	tncAccepted: boolean;
	isArchived?: boolean;
	createdAt?: Date;
	extraInfo?: string;
	paymentStatus?: string;

	invoiceId?: string;
	invoiceUrl?: string;
	invoiceUrlPDF?: string;
}

export interface CandidateProps {
	firstname: string;
	lastname: string;
	employer: string | null;
	jobRole: string | null;
	phone: string;
	email: string;
	addrState: string;
	addrPostcode: string;
}

export interface ReportProps {
	Key: string;
	uri?: string;
}

const calculateNextValidDate = (date: Date): Date => {
	const dayOfWeek = getDay(date);
	let daysToAdd = 1;

	// If it's Friday (5), add 3 days to get to Monday
	if (dayOfWeek === 5) {
		daysToAdd = 3;
	}
	// If it's Saturday (6), add 2 day to get to Monday
	else if (dayOfWeek === 6) {
		daysToAdd = 2;
	}

	const nextDate = addDays(date, daysToAdd);

	const isPublicHoliday = publicHolidays.some((holiday: string) => isSameDay(nextDate, parseISO(holiday)));

	if (isPublicHoliday) {
		return calculateNextValidDate(nextDate);
	}

	// default to 8:30AM - first available assessment time
	return setMilliseconds(setSeconds(setMinutes(setHours(nextDate, 8), 30), 0), 0);
};

const candidateSchema = Yup.object({
	firstname: Yup.string().default('').required('Required'),
	lastname: Yup.string().default('').required('Required'),
	employer: Yup.string().nullable().default(''),
	jobRole: Yup.string().nullable().default(''),
	phone: Yup.string()
		.matches(/^\d{10}$/, 'Must be only digits and 10 characters long')
		.default('')
		.required('Required'),
	email: Yup.string().email('Invalid email').default('').required('Required'),
	addrState: Yup.string().default('').trim().required('Required'),
	addrPostcode: Yup.string().default('').trim().required('Required'),
});

const schema = Yup.object().shape({
	id: Yup.string().default(''),
	assessmentDates: Yup.array().of(Yup.date()).nullable(),
	// for users to choose a preferred date when creating a request
	prefAssessmentDates: Yup.array()
		.of(Yup.date().nullable())
		.max(2)
		.required()
		.default(() => [calculateNextValidDate(new Date()), null])
		.test('no-duplicates', 'Preferred assessment dates must be unique (including time)', (dates) => {
			if (!Array.isArray(dates)) return false;

			const filtered = dates.filter((d): d is Date => d instanceof Date);

			const uniqueDates = filtered.reduce((prev: Date[], next) => {
				const alreadyExists = prev.some((d) => {
					return isEqual(d, next);
				});
				if (!alreadyExists) prev.push(next);
				return prev;
			}, []);

			// if false there is duplicated dates
			return uniqueDates.length === filtered.length;
		}),
	extraInfo: Yup.string().default(null).nullable(),
	status: Yup.mixed<Status>().oneOf(Object.values(status)).default(status.readyForPayment),
	supplierId: Yup.string().default('').required('Please select a test centre.'),
	tests: Yup.array().default([]).min(1).required(),
	candidate: candidateSchema,
	tncAccepted: Yup.boolean()
		.oneOf([true], 'You must accept the terms and conditions.')
		.default(false)
		.required('You must accept the terms and conditions.'),
});

const candidatePersonalInfoSchema = Yup.object({
	candidate: candidateSchema.pick([
		'firstname',
		'lastname',
		'phone',
		'email',
		'employer',
		'jobRole',
		'addrPostcode',
		'addrState',
	]),
});
const testsSchema = schema.pick(['tests']);
const testingCentreSchema = schema.pick(['supplierId']);
const preferredAssessmentDateSchema = schema.pick(['prefAssessmentDates']);
const reviewSchema = schema.pick(['tncAccepted']);
const editSchema = schema.omit(['tncAccepted']);

const editableStatus = [status.readyForPayment, status.readyForSchedule, status.readyForAssessment];

const preferredAssessmentDateLabels = ['Preferred Assessment Date', 'Second Preferred Assessment Date'];

const normalizeAddress = (addrState: CandidateProps['addrState'], addrPostcode: CandidateProps['addrPostcode']) => {
	return `${addrState} ${addrPostcode}`;
};

const isAddressValid = (addrState: CandidateProps['addrState'], addrPostcode: CandidateProps['addrPostcode']) => {
	try {
		schema.validateSyncAt('candidate.addrState', {
			candidate: {
				addrState,
			},
		});

		schema.validateSyncAt('candidate.addrPostcode', {
			candidate: {
				addrPostcode,
			},
		});

		return true;
	} catch (err) {
		return false;
	}
};

const ifMarkTestChecked =
	(requestedTests: TestProps[] = []) =>
	(test: Partial<TestProps> = {}): boolean => {
		return requestedTests.findIndex((t) => t.id?.toString() === test.id?.toString()) > -1;
	};

const formatSupplierDistance = (distanceInMeters: number) => {
	return `${convert(distanceInMeters).from('m').to('km').toFixed(2)}km`;
};

const getReportName = (s3ObjectKey: string) => {
	return s3ObjectKey.split('/').at(-1);
};

// remove custom cast and use another way - see how setting supplier role in user edit is handled
const cast = (params: RequestProps) => {
	const req = schema.cast(params) as RequestProps;

	return {
		...req,
	};
};

const disableAssessmentSchedulling = (currentStatus: Status): boolean => {
	return ([status.readyForApproval, status.cancelled, status.completed] as Status[]).includes(currentStatus);
};

const isAssessmentScheduled = (currentStatus: Status): boolean => {
	return status.readyForAssessment === currentStatus;
};

const isEditable = (currentStatus: Status): boolean => {
	return (editableStatus as Status[]).includes(currentStatus);
};

const isStatusEditingAllowed = (currentStatus: Status): boolean => {
	return ([status.completed, status.cancelled] as Status[]).includes(currentStatus) === false;
};

const getAllowedEditableStatusAdmin = (): Status[] => {
	return [
		status.readyForPayment,
		status.readyForSchedule,
		status.readyForAssessment,
		status.readyForApproval,
		status.completed,
	];
};

const getAllowedEditableStatusSupplier = (): Status[] => {
	return [status.readyForApproval];
};

const disableTestsEditing = (currentStatus: Status): boolean => {
	return (
		[
			status.readyForSchedule,
			status.readyForAssessment,
			status.readyForApproval,
			status.cancelled,
			status.completed,
		] as Status[]
	).includes(currentStatus);
};

export const Request = {
	schema,
	editSchema,
	testsSchema,
	candidatePersonalInfoSchema,
	preferredAssessmentDateSchema,
	reviewSchema,
	testingCentreSchema,

	preferredAssessmentDateLabels,
	status,
	paymentStatus,

	// utility functions
	cast,
	calculateNextValidDate,
	disableAssessmentSchedulling,
	disableTestsEditing,
	formatSupplierDistance,
	getAllowedEditableStatusAdmin,
	getAllowedEditableStatusSupplier,
	getReportName,
	ifMarkTestChecked,
	isAddressValid,
	isAssessmentScheduled,
	isEditable,
	isStatusEditingAllowed,
	normalizeAddress,
};
